/**
 * Utility functions for handling image paths
 */

/**
 * Returns the image path as is, since the paths from the API are already correct
 * @param path The image path from the API
 * @returns The same path, or a placeholder if the path is empty
 */
export const getImageUrl = (path: string | undefined): string => {
  // Handle undefined or empty paths
  if (!path) {
    console.warn('getImageUrl called with empty path, using fallback');
    return '/images/placeholder.png';
  }

  // If it's already a full URL or data URL, return as is
  if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) {
    return path;
  }

  // Return the path as is, assuming it's already correctly formatted
  return path;
};

/**
 * Gets the first image URL from an array of image paths
 * @param images Array of image paths
 * @param defaultImage Default image path if no images are available
 * @returns The URL to the first image
 */
export const getFirstImage = (images: string[] | undefined, defaultImage: string = ''): string => {
  if (!images || images.length === 0) {
    return getImageUrl(defaultImage || 'placeholder.png');
  }
  return getImageUrl(images[0]);
};

/**
 * Checks if an image exists at the given URL
 * @param url The URL to check
 * @returns A promise that resolves to true if the image exists
 */
export const imageExists = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};
